/** Marketing i18n fragment — intgGithub (integrations/github). */

export const intgGithubEn = {
  meta: {
    title: "GitHub integration — repository context and AI-proposed pull requests",
    description:
      "FixControl reads repository and branch context, then delivers proposed changes as pull requests with a reviewable diff and supporting evidence. Your team reviews, merges, and deploys. Per-capability health and an audited outbox back every write.",
  },
  hero: {
    eyebrow: "GitHub integration",
    title: "Patches delivered as PRs,",
    highlight: "reviewed by your team",
    subtitle:
      "FixControl reads repo and branch context, writes a patch, and opens a pull request with the diff and the evidence behind it. Merging and deploying stay with your team.",
    primaryCta: "Book a demo",
    secondaryCta: "See remediation",
    bullet1: "PR delivery only",
    bullet2: "Your team merges",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "What it does",
    title: "From ticket to pull request",
  },
  features: {
    repoContext: {
      title: "Repository & branch context",
      body: "FixControl reads repository and branch context before proposing anything, so a patch is based on the code as it is. Reads stay scoped to the bound project and tenant.",
    },
    proposedPr: {
      title: "Proposed pull requests",
      body: "Where the GitHub App is installed, FixControl opens a pull request carrying the proposed patch. When someone opens the pull request from the patch, they first see its title, description and commit message exactly as GitHub will receive them, and can edit them. It opens PRs; it does not merge them.",
    },
    diff: {
      title: "Diff & evidence",
      body: "The change lands as a normal PR diff with its supporting evidence attached. An engineer reviews it the same way they review any other PR.",
    },
    humanMerge: {
      title: "Human merge & deploy",
      body: "Merging and deploying are decisions your team makes in your own tooling. FixControl’s delivery ends at the open pull request.",
    },
    scoped: {
      title: "Scoped permissions",
      body: "The App acts only within the permissions you grant when you install it. Nothing outside the installation is reachable.",
    },
    audit: {
      title: "Evidence & audit",
      body: "Opening a PR is an attributed, recorded action, so the repository history and the decision ledger line up.",
    },
  },
  caps: {
    eyebrow: "Capabilities & health",
    title: "Read health and write health are tracked separately",
    description:
      "Repo read can be healthy while PR creation is not — the App may be installed on some repositories and not others, or with narrower permissions. FixControl probes each capability and reports it on its own.",
  },
  capabilities: {
    read: {
      name: "Read repo & branch context",
      body: "Reads repository and branch context within the bound project and tenant.",
    },
    openPr: {
      name: "Open PR with proposed patch",
      body: "Requires the GitHub App installed on the target repository with permission to open pull requests. The write dispatches through the integration outbox.",
    },
    diffEvidence: {
      name: "Diff & evidence on the PR",
      body: "Each proposed change ships as a reviewable diff with its evidence, on the PR itself.",
    },
    autoMerge: {
      name: "Merging or deploying on its own",
      body: "Not a capability FixControl has. Delivery is PR-only; merge and deploy remain human actions in your own workflow.",
    },
    singleGreen: {
      name: "A blanket ‘connected’ status",
      body: "Not shown. Repo read and PR-open are probed and reported separately, so a broken write path is visible.",
    },
  },
  governed: {
    eyebrow: "When a PR fails to open",
    title: "If the PR can’t be opened, you’ll know",
    noteTitle: "One queue for every GitHub write",
    noteBody:
      "PR creation dispatches through the integration outbox: retries on transient errors, dead-letter on persistent ones, and a visible entry on the operational timeline either way. A person reviews the diff and merges; the approving decision is stored in the decision ledger with actor, role, channel, evidence, and consequence.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "GitHub questions",
    q1: {
      q: "Does FixControl merge pull requests or deploy on its own?",
      a: "No. FixControl opens a pull request with the proposed patch and stops there. Merging and deploying happen in your workflow, by your team.",
    },
    q2: {
      q: "What does FixControl need to open a PR?",
      a: "The GitHub App installed on the target repository, with permission to open pull requests. If the App isn’t installed there, repo read may still work while PR-open reports unavailable.",
    },
    q3: {
      q: "How does an engineer review a proposed change?",
      a: "As an ordinary pull request: a diff, plus the evidence that produced it. Review happens in GitHub with your normal process.",
    },
    q4: {
      q: "What happens if opening a PR fails?",
      a: "The attempt stays in the outbox and retries; a persistent failure dead-letters and shows on the timeline. The PR either exists or the failure is visible — there’s no third state.",
    },
    q5: {
      q: "Are actions on GitHub audited?",
      a: "Yes. The decision that authorized the PR is in the decision ledger, and the PR itself is in your repository history. The two records match.",
    },
  },
  related: {
    remediation: {
      label: "Support → engineering",
      description: "From support ticket to reviewed PR, with gates in between.",
    },
    gitlab: {
      label: "GitLab",
      description: "The equivalent flow for GitLab, delivered as merge requests.",
    },
    deployment: {
      label: "Deployment governance",
      description: "What happens after merge: gated, human-approved deploys.",
    },
  },
  cta: {
    title: "See a proposed PR land in your review queue",
    description:
      "Book a demo: a ticket becomes a patch, the patch becomes a PR, and the approval trail is there to read.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgGithubNl = {
  meta: {
    title: "GitHub-integratie — repository-context en door AI voorgestelde pull requests",
    description:
      "FixControl leest repository- en branch-context en levert voorgestelde wijzigingen als pull requests met een reviewbare diff en onderbouwend bewijs. Je team reviewt, merget en deployt. Health per capability en een geauditeerde outbox staan achter elke schrijfactie.",
  },
  hero: {
    eyebrow: "GitHub-integratie",
    title: "Patches geleverd als PR’s,",
    highlight: "gereviewd door je team",
    subtitle:
      "FixControl leest repo- en branch-context, schrijft een patch en opent een pull request met de diff en het bewijs erachter. Mergen en deployen blijven bij je team.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk remediatie",
    bullet1: "Alleen PR-levering",
    bullet2: "Je team merget",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Van ticket naar pull request",
  },
  features: {
    repoContext: {
      title: "Repository- & branch-context",
      body: "FixControl leest repository- en branch-context voordat het iets voorstelt, zodat een patch is gebaseerd op de code zoals die is. Leesacties blijven gescoped op het gekoppelde project en de tenant.",
    },
    proposedPr: {
      title: "Voorgestelde pull requests",
      body: "Waar de GitHub App is geïnstalleerd, opent FixControl een pull request met de voorgestelde patch. Wie de pull request vanuit de patch opent, ziet eerst de titel, beschrijving en commitmelding precies zoals GitHub ze ontvangt, en kan die nog aanpassen. Het opent PR’s; mergen doet het niet.",
    },
    diff: {
      title: "Diff & bewijs",
      body: "De wijziging komt binnen als een gewone PR-diff met het onderbouwende bewijs erbij. Een engineer reviewt hem net als elke andere PR.",
    },
    humanMerge: {
      title: "Menselijke merge & deploy",
      body: "Mergen en deployen zijn beslissingen die je team in je eigen tooling neemt. De levering van FixControl eindigt bij de geopende pull request.",
    },
    scoped: {
      title: "Gescopete permissies",
      body: "De App handelt alleen binnen de permissies die je geeft bij de installatie. Niets buiten de installatie is bereikbaar.",
    },
    audit: {
      title: "Bewijs & audit",
      body: "Een PR openen is een toewijsbare, vastgelegde actie, zodat de repository-historie en het beslis-grootboek op elkaar aansluiten.",
    },
  },
  caps: {
    eyebrow: "Capabilities & health",
    title: "Lees-health en schrijf-health worden apart bijgehouden",
    description:
      "Repo-lezen kan gezond zijn terwijl PR-aanmaak dat niet is — de App is misschien op sommige repository’s geïnstalleerd en op andere niet, of met beperktere permissies. FixControl test elke capability en rapporteert die apart.",
  },
  capabilities: {
    read: {
      name: "Repo- & branch-context lezen",
      body: "Leest repository- en branch-context binnen het gekoppelde project en de tenant.",
    },
    openPr: {
      name: "PR openen met voorgestelde patch",
      body: "Vereist de GitHub App op de doel-repository met permissie om pull requests te openen. De schrijfactie loopt via de integratie-outbox.",
    },
    diffEvidence: {
      name: "Diff & bewijs op de PR",
      body: "Elke voorgestelde wijziging komt als reviewbare diff met bewijs, op de PR zelf.",
    },
    autoMerge: {
      name: "Zelf mergen of deployen",
      body: "Geen capability die FixControl heeft. Levering is PR-only; mergen en deployen blijven menselijke acties in je eigen workflow.",
    },
    singleGreen: {
      name: "Eén algemene ‘connected’-status",
      body: "Wordt niet getoond. Repo-lezen en PR-openen worden apart getest en gerapporteerd, zodat een kapot schrijfpad zichtbaar is.",
    },
  },
  governed: {
    eyebrow: "Als een PR niet geopend kan worden",
    title: "Kan de PR niet worden geopend, dan weet je het",
    noteTitle: "Eén wachtrij voor elke GitHub-schrijfactie",
    noteBody:
      "Een PR aanmaken loopt via de integratie-outbox: opnieuw proberen bij tijdelijke fouten, dead-letter bij hardnekkige, en hoe dan ook een zichtbare regel op de operationele tijdlijn. Een persoon reviewt de diff en merget; de goedkeurende beslissing wordt in het beslis-grootboek bewaard met actor, rol, kanaal, bewijs en consequentie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over GitHub",
    q1: {
      q: "Merget FixControl pull requests of deployt het zelf?",
      a: "Nee. FixControl opent een pull request met de voorgestelde patch en houdt daar op. Mergen en deployen gebeuren in je eigen workflow, door je team.",
    },
    q2: {
      q: "Wat heeft FixControl nodig om een PR te openen?",
      a: "De GitHub App op de doel-repository, met permissie om pull requests te openen. Staat de App daar niet, dan kan repo-lezen nog werken terwijl PR-openen onbeschikbaar rapporteert.",
    },
    q3: {
      q: "Hoe reviewt een engineer een voorgestelde wijziging?",
      a: "Als een gewone pull request: een diff, plus het bewijs dat hem opleverde. De review gebeurt in GitHub met je normale proces.",
    },
    q4: {
      q: "Wat gebeurt er als het openen van een PR mislukt?",
      a: "De poging blijft in de outbox en wordt opnieuw geprobeerd; een hardnekkige fout gaat naar de dead-letter en verschijnt op de tijdlijn. De PR bestaat, of de fout is zichtbaar — een derde toestand is er niet.",
    },
    q5: {
      q: "Worden GitHub-acties geaudit?",
      a: "Ja. De beslissing die de PR autoriseerde staat in het beslis-grootboek, en de PR zelf staat in je repository-historie. De twee sluiten op elkaar aan.",
    },
  },
  related: {
    remediation: {
      label: "Support → engineering",
      description: "Van supportticket naar gereviewde PR, met poorten ertussen.",
    },
    gitlab: {
      label: "GitLab",
      description: "Dezelfde flow voor GitLab, geleverd als merge requests.",
    },
    deployment: {
      label: "Deployment-governance",
      description: "Wat na de merge gebeurt: gegate, door mensen goedgekeurde deploys.",
    },
  },
  cta: {
    title: "Zie een voorgestelde PR in je reviewwachtrij belanden",
    description:
      "Boek een demo: een ticket wordt een patch, de patch wordt een PR, en het goedkeuringsspoor is er om te lezen.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
